import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Reveal, MaskedLines } from "@/components/Reveal";
import { REALISATIONS, FILTERS } from "@/data/content";

const Realisations = () => {
  const [filter, setFilter] = useState("tous");
  const items = filter === "tous" ? REALISATIONS : REALISATIONS.filter((r) => r.category === filter);

  return (
    <main data-testid="realisations-page" className="pt-20">
      {/* ——— EN-TÊTE ——— */}
      <section className="relative overflow-hidden circuit-bg grain py-24 sm:py-32">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(15,52,140,0.1),transparent_55%)]" aria-hidden="true" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="flex items-center gap-3 text-xs font-mono uppercase tracking-[0.28em] text-brand-blue"
          >
            <span className="h-px w-10 bg-brand-yellow/70" />
            Galerie de réalisations
          </motion.p>
          <h1 data-testid="realisations-title" className="mt-8 font-display text-4xl sm:text-6xl lg:text-7xl font-black uppercase leading-[0.95] tracking-tight">
            <MaskedLines
              delay={0.3}
              lines={[
                <span key="a" className="text-ink">Le courant</span>,
                <span key="b" className="text-brand-red glow-red">laisse des traces.</span>,
              ]}
            />
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.85 }}
            className="mt-8 max-w-2xl text-base sm:text-lg leading-relaxed text-slate-700"
          >
            Une sélection de chantiers types réalisés par nos soins — photos de terrain et illustrations.
            Nos références détaillées vous sont présentées sur demande.
          </motion.p>
        </div>
      </section>

      {/* ——— FILTRES + GALERIE ——— */}
      <section className="py-20 sm:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <div className="flex flex-wrap gap-3" data-testid="realisations-filters">
              {FILTERS.map((f) => (
                <button
                  key={f.id}
                  data-testid={`filter-btn-${f.id}`}
                  onClick={() => setFilter(f.id)}
                  className={`rounded-full px-5 py-2.5 text-sm font-semibold transition-all duration-300 ${
                    filter === f.id
                      ? "bg-brand-blue text-white shadow-[0_8px_24px_rgba(15,52,140,0.3)]"
                      : "border border-slate-300 text-slate-700 hover:border-brand-blue/50 hover:text-brand-blue"
                  }`}
                >
                  {f.label}
                </button>
              ))}
            </div>
          </Reveal>

          <motion.div layout className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3" data-testid="realisations-grid">
            <AnimatePresence mode="popLayout">
              {items.map((r, i) => (
                <motion.article
                  key={r.id}
                  layout
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  transition={{ duration: 0.5, delay: i * 0.05, ease: [0.16, 1, 0.3, 1] }}
                  data-testid={`realisation-item-${r.id}`}
                  className="group relative overflow-hidden rounded-2xl border border-slate-200 bg-brand-card"
                >
                  <div className="overflow-hidden">
                    <img
                      src={r.img}
                      alt={r.title}
                      loading="lazy"
                      className="aspect-[4/3] w-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                  </div>
                  <div className="p-6">
                    <h2 className="font-display text-xl font-bold text-ink transition-colors duration-300 group-hover:text-brand-blue">
                      {r.title}
                    </h2>
                    <div className="mt-4 flex flex-wrap gap-2">
                      {r.tags.map((t) => (
                        <span key={t} className="rounded-full border border-slate-200 bg-brand-ink px-3 py-1 text-xs text-slate-600">
                          {t}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div className="pointer-events-none absolute inset-0 rounded-2xl border border-transparent transition-colors duration-500 group-hover:border-brand-blue/40" aria-hidden="true" />
                </motion.article>
              ))}
            </AnimatePresence>
          </motion.div>
        </div>
      </section>
    </main>
  );
};

export default Realisations;
